import { useState } from 'react';
import styled from '@emotion/styled';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface SegmentOverviewCardProps {
  narrative?: string;
  commercialAction?: string;
  gapAnalysis?: string | Record<string, unknown>;
}

export default function SegmentOverviewCard({ narrative, commercialAction, gapAnalysis }: SegmentOverviewCardProps): React.JSX.Element | null {
  const [expanded, setExpanded] = useState(true);

  if (!narrative && !commercialAction && !gapAnalysis) return null;

  return (
    <Root>
      <Header onClick={() => setExpanded(!expanded)}>
        <span>Segment Overview</span>
        <ExpandIcon $expanded={expanded}><ExpandMoreIcon /></ExpandIcon>
      </Header>
      {expanded && (
        <Content>
          {narrative && (
            <Narrative>{narrative}</Narrative>
          )}
          {(commercialAction || gapAnalysis) && (
            <CardRow>
              {commercialAction && (
                <ActionCard>
                  <CardLabel style={{ color: '#2d7a2e' }}>Commercial Action</CardLabel>
                  {commercialAction}
                </ActionCard>
              )}
              {gapAnalysis && (
                <GapCard>
                  <CardLabel style={{ color: '#92400e' }}>Data Gaps</CardLabel>
                  {typeof gapAnalysis === 'string'
                    ? gapAnalysis
                    : Object.entries(gapAnalysis).map(([k, v]) => (
                        <div key={k}><strong style={{ textTransform: 'capitalize' }}>{k}:</strong> {String(v)}</div>
                      ))
                  }
                </GapCard>
              )}
            </CardRow>
          )}
        </Content>
      )}
    </Root>
  );
}

const Root = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const Header = styled.div`
  padding: 8px 16px;
  font-size: 20px;
  font-weight: 700;
  font-family: var(--font-bold, inherit);
  color: var(--black-l, #323232);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
  min-height: 64px;
  box-sizing: border-box;
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  color: rgba(0, 0, 0, 0.54);
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${p => p.$expanded ? '180deg' : '0deg'});
  display: flex;
`;

const Content = styled.div`
  padding: 0 16px 16px;
`;

const Narrative = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #4c4c4c;
  margin-bottom: 16px;
`;

const CardRow = styled.div`
  display: flex;
  gap: 16px;
`;

const ActionCard = styled.div`
  flex: 1;
  border-radius: 8px;
  padding: 14px;
  background: #e3f9e8;
  border: 1px solid #4CB847;
  font-size: 13px;
  line-height: 1.5;
  color: #4c4c4c;
`;

const GapCard = styled.div`
  flex: 1;
  border-radius: 8px;
  padding: 14px;
  background: #fffbeb;
  border: 1px solid #f59e0b;
  font-size: 13px;
  line-height: 1.5;
  color: #4c4c4c;
`;

const CardLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
`;
